import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { getShiftDisplayDate } from "../../../utils/dateUtils";
import { SystemValidationTextField, type ValidationTextFieldProps } from "../../UIToolkit/Inputs/SystemValidationTextField";

type Props = {
	initialDate: DateTime;
	minimalDate?: DateTime;
	onDateSelectionChange: (date: DateTime) => void;
	mode: "delete" | "edit";
	onValidChange?: (isValid: boolean) => void;
	disabled?: boolean;
};

export const DeleteUpdateDateSelection: FC<Props> = ({ onDateSelectionChange, initialDate, minimalDate, mode, onValidChange, disabled }) => {
	const { chosenDepartment } = useHospitalData();
	const { t } = useTranslation();
	const logicalDate = useMemo(
		() => getShiftDisplayDate(initialDate, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day"),
		[initialDate, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);
	const [selectedDate, setSelectedDate] = useState<DateTime>(logicalDate);

	useEffect(() => setSelectedDate(logicalDate), [logicalDate]);

	const validation = useMemo<ValidationTextFieldProps>(
		() => ({
			performValidation: "always",
			showValidWhenValid: "never",
			helperText: {
				valid: t("valid.date"),
				invalid: t("date.may.not.be.in.past"),
			},
			validationCallback: (value) => {
				const dateValue = DateTime.fromISO(value);
				const valid =
					dateValue.isValid && dateValue >= (minimalDate && minimalDate < initialDate ? minimalDate : initialDate).startOf("day").minus({ days: 1 });
				onValidChange?.(valid);
				return valid;
			},
		}),
		[onValidChange, minimalDate, initialDate, t],
	);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "start",
				justifyContent: mode === "edit" ? "end" : "center",
				flexDirection: "row",
				gap: 1,
				width: "100%",
			}}
		>
			<Typography
				sx={{
					pt: 1,
				}}
			>
				{mode === "delete" ? t("delete.shift.from") : t("apply.changes.from")}
			</Typography>
			<SystemValidationTextField
				disabled={disabled}
				value={selectedDate?.toFormat("yyyy-MM-dd") ?? ""}
				inputType={"date"}
				validation={validation}
				formControlSx={{ width: "200px" }}
				onChange={(event) => {
					const dateValue = DateTime.fromISO(event.target.value);
					if (dateValue.isValid) {
						setSelectedDate(dateValue);
						onDateSelectionChange(dateValue);
					}
				}}
				inputProps={{
					inputProps: {
						min: (minimalDate ?? initialDate).toFormat("yyyy-MM-dd"),
					},
				}}
			/>
			<Typography
				sx={{
					pt: 1,
				}}
			>
				{t("and.following.dates")}
			</Typography>
		</Box>
	);
};
